// Step 2's customer picker. Search-first, by design.
//
// The BDD cannot create a customer without first seeing what exists, because
// "create a new customer" is the last row of the results list rather than a control
// beside it. That ordering is the guardrail; a tooltip cannot be one.
//
// Matching runs server-side in nas_search_customers, which composes on search_entities.
// Nothing here re-implements matching, and the word "alias" never reaches the screen —
// it is a machine reconciliation layer for QBO and AP, not a BDD field.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import {
  INTAKE_INPUT_CLS,
  CustomerResultRow,
  CreateCustomerRow,
  DuplicateWarning,
  type CustomerMatch,
} from './intake-parts';

const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

export type CustomerChoice = {
  /** Empty when the BDD chose to create a new customer. */
  account_id: string;
  account_name: string;
  parent_id: string | null;
  /** True only when this will mint a customer on submit. */
  isNew: boolean;
};

export default function CustomerPicker({
  value,
  onChange,
  parentOptions,
  disabled,
}: {
  value: CustomerChoice | null;
  onChange: (c: CustomerChoice | null) => void;
  parentOptions: { parent_id: string; parent_name: string }[];
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerMatch[]>([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<CustomerMatch | null>(null);
  const [flagged, setFlagged] = useState(false);
  const [dismissedDup, setDismissedDup] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newParentId, setNewParentId] = useState('');
  const debounceRef = useRef<number | undefined>(undefined);

  const run = useCallback(async (q: string) => {
    if (q.trim().length < MIN_QUERY) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    setErr(null);
    const { data, error } = await supabase.rpc('nas_search_customers', {
      p_query: q.trim(),
      p_limit: 8,
    });
    if (error) {
      setErr(errorMessage(error));
      setResults([]);
    } else {
      setResults((data ?? []) as CustomerMatch[]);
    }
    setSearching(false);
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => void run(query), SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, run]);

  const pick = (m: CustomerMatch) => {
    setPicked(m);
    setCreatingNew(false);
    setFlagged(false);
    setDismissedDup(false);
    onChange({
      account_id: m.account_id,
      account_name: m.account_name,
      parent_id: m.parent_id,
      isNew: false,
    });
  };

  const startCreate = () => {
    setPicked(null);
    setCreatingNew(true);
    onChange({
      account_id: '',
      account_name: query.trim(),
      parent_id: null,
      isNew: true,
    });
  };

  const clear = () => {
    setPicked(null);
    setCreatingNew(false);
    setFlagged(false);
    setDismissedDup(false);
    onChange(null);
  };

  // Already chosen — show what was chosen, with one way back.
  if (value && !creatingNew && picked) {
    return (
      <div className="rounded-md border border-tcs-border bg-tcs-surface p-3">
        <div className="flex items-start gap-2">
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-tcs-text">
              {picked.account_name}
            </span>
            <span className="block text-xs text-tcs-text-muted">
              {picked.account_id} · {picked.site_count === 1 ? '1 site' : `${picked.site_count} sites`}
              {picked.is_msa === 'Yes' ? ' · MSA in place' : ''}
              {picked.parent_name ? ` · rolls up to ${picked.parent_name}` : ''}
            </span>
          </span>
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            className="ml-auto shrink-0 text-xs font-medium text-tcs-text-muted underline hover:text-tcs-text"
          >
            Change
          </button>
        </div>
        {picked.likely_duplicate_of && !dismissedDup && (
          <div className="mt-3">
            <DuplicateWarning
              picked={picked}
              flagged={flagged}
              onFlag={() => setFlagged(true)}
              onDismiss={() => setDismissedDup(true)}
            />
          </div>
        )}
      </div>
    );
  }

  // Creating one deliberately — the parent-company question, which is the first time
  // corporate_parents has ever been reachable from this form.
  if (creatingNew) {
    return (
      <div className="rounded-md border border-tcs-border bg-tcs-surface p-3">
        <p className="m-0 text-sm font-semibold text-tcs-text">
          New customer: {value?.account_name || '—'}
        </p>
        <label className="mb-1 mt-3 block text-xs font-semibold text-tcs-text">
          Does it roll up to a parent company?
        </label>
        <select
          value={newParentId}
          disabled={disabled}
          onChange={(e) => {
            setNewParentId(e.target.value);
            onChange({
              account_id: '',
              account_name: value?.account_name ?? query.trim(),
              parent_id: e.target.value || null,
              isNew: true,
            });
          }}
          className={INTAKE_INPUT_CLS}
        >
          <option value="">None — standalone customer</option>
          {parentOptions.map((p) => (
            <option key={p.parent_id} value={p.parent_id}>
              {p.parent_name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={clear}
          disabled={disabled}
          className="mt-3 text-xs font-medium text-tcs-text-muted underline hover:text-tcs-text"
        >
          Back to search
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-tcs-text-faint" />
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Start typing the customer’s name…"
          className={`${INTAKE_INPUT_CLS} pl-9`}
          autoComplete="off"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-tcs-text-faint" />
        )}
      </div>

      {err && (
        <p className="mt-2 text-xs text-rag-red" role="alert">
          {err}
        </p>
      )}

      {query.trim().length >= MIN_QUERY && !searching && (
        <ul className="mt-2 overflow-hidden rounded-md border border-tcs-border bg-tcs-bg">
          <li className="border-b border-tcs-border bg-tcs-surface px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-faint">
            {results.length === 0
              ? 'No existing customer matches'
              : `Existing customers — ${results.length} ${results.length === 1 ? 'match' : 'matches'}`}
          </li>
          {results.map((m) => (
            <CustomerResultRow
              key={m.account_id}
              match={m}
              selected={value?.account_id === m.account_id}
              onPick={pick}
            />
          ))}
          <CreateCustomerRow typed={query} onCreate={startCreate} />
        </ul>
      )}
    </div>
  );
}
